<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import ContactSelector from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CreateCampaign/ContactSelector.vue';
import TemplateSelector from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CreateCampaign/TemplateSelector.vue';

const { t } = useI18n();
const [showSMSCampaignDialog, toggleSMSCampaignDialog] = useToggle();

const campaignTitle = ref('');
const selectedContacts = ref([]);
const selectedTemplate = ref({ name: '', language: '' });
const selectedWhatsappNumber = ref('');
const whatsappNumbers = ref([]);

const route = useRoute();
const accountId = route.params.accountId;
const selectedDate = ref('');
const sendOption = ref('now');

const pad = n => (n < 10 ? '0' + n : n);
const getDefaultDateTime = () => {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

watch(sendOption, () => {
  if (sendOption.value === 'later') {
    selectedDate.value = getDefaultDateTime();
  }
});

const fetchWhatsappNumbers = async () => {
  const res = await fetch(
    `/api/v1/custom_querys/whatsapp_numbers?account_id=${accountId}`,
    {
      headers: {
        custom_access_token: '1a2b3c4d5e',
      },
    }
  );
  if (res.ok) {
    const data = await res.json();
    whatsappNumbers.value = data.phone_numbers;
    selectedWhatsappNumber.value = data.phone_numbers[0] || '';
  } else {
    alert('❌ No se pudieron cargar los números de WhatsApp');
  }
};

const createCronCampaign = async () => {
  const sendAtUtc =
    sendOption.value === 'now'
      ? new Date().toISOString()
      : new Date(selectedDate.value).toISOString();

  const payload = {
    campaign: {
      account_id: accountId,
      title: campaignTitle.value,
      whatsapp_number: selectedWhatsappNumber.value,
      phone_numbers: selectedContacts.value,
      template_name: selectedTemplate.value.name,
      template_language: selectedTemplate.value.language,
      send_at: sendAtUtc,
    },
  };

  const response = await fetch('/api/v1/cron_campaigns', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Error al crear campaña: ${error.error || response.status}`);
  }

  const result = await response.json();
  return result;
};

const handleSubmit = async () => {
  if (!selectedContacts.value.length) {
    alert('Debes seleccionar al menos un contacto');
    return;
  }

  if (!selectedTemplate.value.name || !selectedTemplate.value.language) {
    alert('Debes seleccionar una plantilla válida');
    return;
  }

  if (!selectedWhatsappNumber.value) {
    alert('Debes seleccionar un número de WhatsApp');
    return;
  }

  if (sendOption.value === 'later' && !selectedDate.value) {
    alert('Debes seleccionar la fecha y hora de envío');
    return;
  }

  try {
    await createCronCampaign();
    alert('✅ Campaña programada correctamente');
    toggleSMSCampaignDialog(false);
  } catch (error) {
    console.error('❌ Error al programar campaña:', error);
    alert('❌ Error al programar campaña');
  }
};

onMounted(() => {
  fetchWhatsappNumbers();
  // Si el usuario entra y la opción es 'later', pon fecha por defecto
  if (sendOption.value === 'later') {
    selectedDate.value = getDefaultDateTime();
  }
});
</script>

<template>
  <CampaignLayout
    :header-title="'Nueva campaña de WhatsApp'"
    :button-label="'Programar campaña'"
    @click="handleSubmit"
    @close="toggleSMSCampaignDialog(false)"
  >
    <div class="flex flex-col gap-6 pt-6 px-4 max-w-3xl">
      <div>
        <label for="title" class="block font-medium mb-1">Nombre de la campaña</label>
        <input
          id="title"
          type="text"
          v-model="campaignTitle"
          placeholder="Ej. Campaña de bienvenida"
          class="border px-3 py-2 rounded w-full"
        />
      </div>

      <div>
        <label for="whatsapp_number" class="block font-medium mb-1">Número de WhatsApp</label>
        <select
          id="whatsapp_number"
          v-model="selectedWhatsappNumber"
          class="border px-3 py-2 rounded w-full"
        >
          <option disabled value="">Selecciona un número</option>
          <option
            v-for="number in whatsappNumbers"
            :key="number"
            :value="number"
          >
            {{ number }}
          </option>
        </select>
      </div>

      <ContactSelector v-model:contacts="selectedContacts" />
      <TemplateSelector v-model:template="selectedTemplate" v-model:selectedWhatsappNumber="selectedWhatsappNumber" />

      <div>
        <label class="block font-medium mb-1">Tipo de envío</label>
        <select v-model="sendOption" class="border px-3 py-2 rounded w-full">
          <option value="now">Enviar ahora</option>
          <option value="later">Programar envío</option>
        </select>
      </div>

      <div v-if="sendOption === 'later'">
        <label for="datetime" class="block font-medium mb-1 mt-4">Fecha y hora de envío</label>
        <input
          id="datetime"
          type="datetime-local"
          v-model="selectedDate"
          class="border px-3 py-2 rounded w-full"
        />
      </div>
    </div>
  </CampaignLayout>
</template>
